/**
 * Funções utilitárias para controle da interface do usuário
 */

import React from 'react';

/**
 * Abre a barra lateral nos dispositivos móveis
 */
export function openSidebar() {
  if (typeof document !== 'undefined') {
    document.body.style.overflow = 'hidden';
    document.documentElement.style.setProperty('--SideNavigation-slideIn', '1');
  }
}

/**
 * Fecha a barra lateral nos dispositivos móveis
 */
export function closeSidebar() {
  if (typeof document !== 'undefined') {
    document.documentElement.style.setProperty('--SideNavigation-slideIn', '0');
    document.body.style.removeProperty('overflow');
  }
}

/**
 * Alterna a visibilidade da barra lateral
 */
export function toggleSidebar() {
  if (typeof document !== 'undefined') {
    const slideIn = document.documentElement.style.getPropertyValue('--SideNavigation-slideIn');
    if (slideIn === '1') {
      closeSidebar();
    } else {
      openSidebar();
    }
  }
}

/**
 * Estilo personalizado para scrollbar, a ser usado nos componentes MUI Joy
 */
export const customScrollbarStyle = {
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(0,0,0,0.2)',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'rgba(0,0,0,0.05)',
  },
};

/**
 * Formata um CPF no formato 000.000.000-00
 * @param value String contendo o CPF
 * @returns CPF formatado
 */
export const formatCPF = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  const limit = Math.min(digits.length, 11);
  let result = '';

  for (let i = 0; i < limit; i++) {
    if (i === 3 || i === 6) result += '.';
    if (i === 9) result += '-';
    result += digits[i];
  }

  return result;
};

/**
 * Remove formatação do RG e retorna apenas números e letras
 * @param value String contendo o RG
 * @returns RG sem formatação (máximo 15 caracteres)
 */
export const formatRG = (value: string): string => {
  if (!value) return '';
  // Remove tudo exceto números e letras, converte para maiúscula
  const cleaned = value.replace(/[^\w]/g, '').toUpperCase();
  // Limita a 15 caracteres para novos formatos de RG
  return cleaned.slice(0, 15);
};

/**
 * Formata um CNPJ no formato 00.000.000/0000-00
 * @param value String contendo o CNPJ
 * @returns CNPJ formatado
 */
export const formatCNPJ = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  const limit = Math.min(digits.length, 14);
  let result = '';

  for (let i = 0; i < limit; i++) {
    if (i === 2 || i === 5) result += '.';
    if (i === 8) result += '/';
    if (i === 12) result += '-';
    result += digits[i];
  }

  return result;
};

/**
 * Formata um CEP no formato 00000-000
 * @param value String contendo o CEP
 * @returns CEP formatado
 */
export const formatCEP = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  const limit = Math.min(digits.length, 8);
  let result = '';

  for (let i = 0; i < limit; i++) {
    if (i === 5) result += '-';
    result += digits[i];
  }

  return result;
};

/**
 * Formata um número de telefone
 * @param value String contendo o telefone
 * @returns Telefone formatado
 */
export const formatPhone = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  const limit = Math.min(digits.length, 11);
  let result = '';

  if (limit > 0) {
    result += '(';
    for (let i = 0; i < limit; i++) {
      if (i === 2) result += ') ';
      if (i === 7) result += '-';
      result += digits[i];
    }
  }

  return result;
};

/**
 * Formata um valor monetário
 * @param value Valor a ser formatado
 * @returns Valor formatado como moeda brasileira
 */
export const formatCurrency = (value: string | number): string => {
  if (typeof value === 'string') {
    value = parseFloat(value.replace(/\D/g, '')) / 100;
  }
  
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value || 0);
};

/**
 * Formata um valor de área em hectares
 * @param value Valor a ser formatado
 * @returns Valor formatado com 1 casa decimal
 */
export const formatArea = (value: string | number): string => {
  if (typeof value === 'string') {
    value = value.replace(/[^\d.,]/g, '').replace(',', '.');
    value = parseFloat(value);
  }
  
  return value ? value.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '';
};

/**
 * Formata números de registro como matrícula, CCIR, CAR, etc.
 * @param value String contendo o número de registro
 * @returns Número de registro formatado
 */
export const formatRegistration = (value: string): string => {
  // Remove caracteres especiais, deixando apenas alfanuméricos
  const cleaned = value.replace(/[^\w]/g, '').toUpperCase();
  
  // Agrupa em blocos de 4 caracteres para melhor visualização
  let result = '';
  for (let i = 0; i < cleaned.length; i++) {
    if (i > 0 && i % 4 === 0) result += '-';
    result += cleaned[i];
  }
  
  return result;
};

/**
 * Formata uma data para o formato brasileiro (DD/MM/AAAA)
 * @param value Data em formato ISO (AAAA-MM-DD) ou Date
 * @returns Data formatada no padrão brasileiro
 */
export const formatDate = (value: string | Date): string => {
  if (!value) return 'Não informado';
  
  try {
    const date = typeof value === 'string' ? new Date(value) : value;
    
    if (isNaN(date.getTime())) {
      return 'Data inválida';
    }
    
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch (error) {
    return 'Data inválida';
  }
};

/**
 * Cria um handler para inputs com máscara
 * @param onChange Função de callback para o onChange do input
 * @param formatFunction Função que formata o valor
 * @returns Função handler para ser usada no onChange do input
 */
export const createMaskedInputHandler = (
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
  formatFunction: (value: string) => string
) => {
  return (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const { value, selectionStart } = input;
    const formattedValue = formatFunction(value);
    
    // Calcular a nova posição do cursor após a formatação
    const cursorPosition = selectionStart;
    const oldValueLength = value.length;
    const newValueLength = formattedValue.length;
    const cursorOffset = newValueLength - oldValueLength;
    
    // Atualizar o valor do input
    input.value = formattedValue;
    
    // Manter a posição do cursor após a formatação
    if (cursorPosition !== null) {
      const newPosition = cursorPosition + (cursorOffset > 0 ? cursorOffset : 0);
      input.setSelectionRange(newPosition, newPosition);
    }
    
    // Chamar o callback original
    onChange(e);
  };
};

/**
 * Formata uma data no formato DD/MM/AAAA com máscara inteligente
 * @param value String contendo a data
 * @returns Data formatada com máscara DD/MM/AAAA
 */
export const formatDateMask = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  const limit = Math.min(digits.length, 8);
  let result = '';

  for (let i = 0; i < limit; i++) {
    if (i === 2 || i === 4) result += '/';
    result += digits[i];
  }

  return result;
};

/**
 * Valida se uma data no formato DD/MM/AAAA é válida
 * @param value String contendo a data no formato DD/MM/AAAA
 * @returns true se a data for válida, false caso contrário
 */
export const isValidDate = (value: string): boolean => {
  if (!value || value.length !== 10) return false;
  
  const [day, month, year] = value.split('/').map(Number);
  
  if (!day || !month || !year) return false;
  if (day < 1 || day > 31) return false;
  if (month < 1 || month > 12) return false;
  if (year < 1900 || year > 2100) return false;
  
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && 
         date.getMonth() === month - 1 && 
         date.getDate() === day;
};

/**
 * Converte uma data do formato DD/MM/AAAA para ISO (AAAA-MM-DD)
 * @param value String contendo a data no formato DD/MM/AAAA
 * @returns Data no formato ISO ou string vazia se inválida
 */
export const convertDateToISO = (value: string): string => {
  if (!value || !isValidDate(value)) return '';
  
  const [day, month, year] = value.split('/');
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
};

/**
 * Converte uma data do formato ISO (AAAA-MM-DD) para DD/MM/AAAA
 * @param value String contendo a data no formato ISO
 * @returns Data no formato DD/MM/AAAA ou string vazia se inválida
 */
export const convertDateFromISO = (value: string): string => {
  if (!value) return '';
  
  try {
    const date = new Date(value + 'T00:00:00');
    if (isNaN(date.getTime())) return '';
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString();
    
    return `${day}/${month}/${year}`;
  } catch (error) {
    return '';
  }
};

/**
 * Cria um handler para inputs de data com máscara DD/MM/AAAA
 * @param onChange Função de callback para o onChange do input
 * @returns Função handler para ser usada no onChange do input de data
 */
export const createDateMaskHandler = (
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
) => {
  return (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const { value, selectionStart } = input;
    const formattedValue = formatDateMask(value);
    
    const cursorPosition = selectionStart;
    const oldValueLength = value.length;
    const newValueLength = formattedValue.length;
    const cursorOffset = newValueLength - oldValueLength;
    
    input.value = formattedValue;
    
    if (cursorPosition !== null) {
      const newPosition = Math.min(
        cursorPosition + (cursorOffset > 0 ? cursorOffset : 0),
        formattedValue.length
      );
      input.setSelectionRange(newPosition, newPosition);
    }
    
    onChange(e);
  };
};

/**
 * Garante que uma data em formato DD/MM/YYYY tenha zeros à esquerda
 * @param value String contendo a data no formato D/M/YYYY ou DD/MM/YYYY
 * @returns Data formatada garantindo DD/MM/YYYY
 */
export const ensureDateFormat = (value: string): string => {
  if (!value) return 'Não informado';
  
  // Se já está no formato DD/MM/YYYY correto, retorna como está
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
    return value;
  }
  
  // Se está no formato D/M/YYYY ou DD/M/YYYY etc, corrige
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(value)) {
    const [day, month, year] = value.split('/');
    return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
  }
  
  // Se está em formato ISO, converte
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
    return convertDateFromISO(value);
  }
  
  // Tenta criar uma data e formatar
  try {
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      return formatDate(date);
    }
  } catch (error) {
    // Ignora erro e continua
  }
  
  return value; // Retorna como estava se não conseguir formatar
};

/**
 * Formata área total com máscara flexível
 * Aplica formatação apenas quando necessário, sem interferir na digitação
 */
export const formatAreaInput = (value: string): string => {
  if (!value) return '';
  
  // Remove tudo exceto números, vírgula e ponto
  let cleaned = value.replace(/[^\d,\.]/g, '');
  
  // Substitui ponto por vírgula
  cleaned = cleaned.replace('.', ',');
  
  // Se há múltiplas vírgulas, manter apenas a primeira
  const commaIndex = cleaned.indexOf(',');
  if (commaIndex !== -1) {
    const beforeComma = cleaned.substring(0, commaIndex);
    const afterComma = cleaned.substring(commaIndex + 1).replace(/,/g, '');
    // Limitar casas decimais a 4
    const limitedAfter = afterComma.substring(0, 4);
    cleaned = beforeComma + ',' + limitedAfter;
  }
  
  return cleaned;
};

/**
 * Formata área para exibição final (adiciona ,0000 se necessário)
 */
export const formatAreaDisplay = (value: string): string => {
  if (!value) return '';
  
  let formatted = formatAreaInput(value);
  
  // Se não tem vírgula e tem números, adicionar ,0000
  if (!formatted.includes(',') && formatted.length > 0) {
    formatted = formatted + ',0000';
  } else if (formatted.includes(',')) {
    // Se tem vírgula, completar com zeros até 4 casas decimais se necessário
    const [intPart, decPart] = formatted.split(',');
    const paddedDec = (decPart || '').padEnd(4, '0').substring(0, 4);
    formatted = intPart + ',' + paddedDec;
  }
  
  return formatted;
};

/**
 * Formata valor da propriedade durante a digitação
 * Permite digitação livre, aplica R$ e formatação apenas quando apropriado
 */
export const formatPropertyInput = (value: string): string => {
  if (!value) return '';
  
  // Remove R$ e espaços primeiro
  let cleaned = value.replace(/R\$\s*/g, '');
  
  // Remove tudo exceto números, vírgula e ponto
  cleaned = cleaned.replace(/[^\d,\.]/g, '');
  
  // Substitui ponto usado como decimal por vírgula
  const lastDotIndex = cleaned.lastIndexOf('.');
  const lastCommaIndex = cleaned.lastIndexOf(',');
  
  // Se o último ponto está depois da última vírgula, é decimal
  if (lastDotIndex > lastCommaIndex) {
    cleaned = cleaned.substring(0, lastDotIndex) + ',' + cleaned.substring(lastDotIndex + 1);
  }
  
  // Remove pontos extras (que não são decimais)
  cleaned = cleaned.replace(/\./g, '');
  
  // Se há múltiplas vírgulas, manter apenas a primeira
  const commaIndex = cleaned.indexOf(',');
  if (commaIndex !== -1) {
    const beforeComma = cleaned.substring(0, commaIndex);
    const afterComma = cleaned.substring(commaIndex + 1).replace(/,/g, '');
    // Limitar casas decimais a 2
    const limitedAfter = afterComma.substring(0, 2);
    cleaned = beforeComma + ',' + limitedAfter;
  }
  
  return cleaned;
};

/**
 * Formata valor da propriedade para exibição final (com R$ e separadores)
 */
export const formatPropertyDisplay = (value: string): string => {
  if (!value) return '';
  
  let formatted = formatPropertyInput(value);
  
  // Se não tem vírgula e tem números, adicionar ,00
  if (!formatted.includes(',') && formatted.length > 0) {
    formatted = formatted + ',00';
  } else if (formatted.includes(',')) {
    // Se tem vírgula, completar com zeros até 2 casas decimais se necessário
    const [intPart, decPart] = formatted.split(',');
    const paddedDec = (decPart || '').padEnd(2, '0').substring(0, 2);
    formatted = intPart + ',' + paddedDec;
  }
  
  // Adicionar separadores de milhares na parte inteira
  if (formatted.includes(',')) {
    const [intPart, decPart] = formatted.split(',');
    if (intPart) {
      const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      formatted = formattedInt + ',' + decPart;
    }
  }
  
  // Adicionar R$ no início
  return formatted ? 'R$ ' + formatted : '';
};

/**
 * Converte valor formatado de área para número
 */
export const parseAreaValue = (formattedValue: string): number | undefined => {
  if (!formattedValue) return undefined;
  
  // Remove formatação e converte vírgula para ponto
  const cleanValue = formattedValue.replace(/[^\d,]/g, '').replace(',', '.');
  const numValue = parseFloat(cleanValue);
  
  return isNaN(numValue) ? undefined : numValue;
};

/**
 * Converte valor formatado de propriedade para número
 */
export const parsePropertyValue = (formattedValue: string): number | undefined => {
  if (!formattedValue) return undefined;
  
  // Remove R$, pontos de milhares e converte vírgula para ponto
  const cleanValue = formattedValue
    .replace(/R\$\s*/g, '')
    .replace(/\./g, '')
    .replace(',', '.');
    
  const numValue = parseFloat(cleanValue);
  
  return isNaN(numValue) ? undefined : numValue;
};

/**
 * Converte número para string formatada de área (sem formatação adicional)
 */
export const numberToAreaString = (value: number | undefined): string => {
  if (value === undefined || value === null) return '';
  
  // Converte para string preservando as casas decimais originais
  const strValue = value.toString();
  
  // Substitui ponto por vírgula se necessário
  return strValue.replace('.', ',');
};

/**
 * Converte número para string formatada de valor da propriedade (sem formatação adicional)
 */
export const numberToPropertyValueString = (value: number | undefined): string => {
  if (value === undefined || value === null) return '';
  
  // Converte para string preservando as casas decimais originais
  const strValue = value.toString();
  
  // Substitui ponto por vírgula se necessário
  return strValue.replace('.', ',');
};

/**
 * Handler para input de área com máscara flexível
 */
export const createAreaMaskHandler = (
  onChange: (value: number | undefined) => void,
  setDisplayValue: (value: string) => void
) => {
  return (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const { value } = input;
    
    // Durante a digitação, aplicar apenas formatação básica
    const formattedValue = formatAreaInput(value);
    
    // Converter para número e chamar onChange
    const numericValue = parseAreaValue(formattedValue);
    onChange(numericValue);
    
    // Atualizar o valor de display
    setDisplayValue(formattedValue);
  };
};

/**
 * Handler para onBlur de área (aplica formatação completa)
 */
export const createAreaBlurHandler = (
  onChange: (value: number | undefined) => void,
  setDisplayValue: (value: string) => void
) => {
  return (e: React.FocusEvent<HTMLInputElement>) => {
    const input = e.target;
    const { value } = input;
    
    // Aplicar formatação completa quando perder o foco
    const formattedValue = formatAreaDisplay(value);
    
    // Converter para número e chamar onChange
    const numericValue = parseAreaValue(formattedValue);
    onChange(numericValue);
    
    // Atualizar o valor de display
    setDisplayValue(formattedValue);
  };
};

/**
 * Handler para input de valor da propriedade com máscara flexível
 */
export const createPropertyValueMaskHandler = (
  onChange: (value: number | undefined) => void,
  setDisplayValue: (value: string) => void
) => {
  return (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const { value } = input;
    
    // Durante a digitação, aplicar apenas formatação básica
    const formattedValue = formatPropertyInput(value);
    
    // Converter para número e chamar onChange
    const numericValue = parsePropertyValue(formattedValue);
    onChange(numericValue);
    
    // Atualizar o valor de display
    setDisplayValue(formattedValue);
  };
};

/**
 * Handler para onBlur de valor da propriedade (aplica formatação completa)
 */
export const createPropertyValueBlurHandler = (
  onChange: (value: number | undefined) => void,
  setDisplayValue: (value: string) => void
) => {
  return (e: React.FocusEvent<HTMLInputElement>) => {
    const input = e.target;
    const { value } = input;
    
    // Aplicar formatação completa quando perder o foco
    const formattedValue = formatPropertyDisplay(value);
    
    // Converter para número e chamar onChange
    const numericValue = parsePropertyValue(formattedValue);
    onChange(numericValue);
    
    // Atualizar o valor de display
    setDisplayValue(formattedValue);
  };
};
